import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import React, { useState, useEffect, useRef } from "react";
import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import useZustandStore from "zustandStore";

import SearchInput from "components/SearchInput";
import { SolidButton } from "components/buttons/SolidButton";
import TableField from "components/Table";
import ProfileIpAddressModal from "./Modal";
import ConfirmDeleteModal from "./Modal/ConfirmDeleteModal";
import Loading from "./loading";
import GlobalAlert from "components/Notification";

import Api from "./util/api";
import { getTableColumns, fromJSON } from "./util/func";

const ProfileIpAddress = ({ showEditGeneral, onIpAddressProfileCountChange }) => {
  const theme = useTheme();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const columnHelper = createColumnHelper();

  const { auth, isAuthReady } = useAuthScope();
  const [loading, setLoading] = useState(false);
  const [uuidList, _setUuidList] = useState({
    read: uuidv4(),
    create: uuidv4(),
    update: uuidv4(),
    delete: uuidv4(),
  });
  const [originIpAddresses, setOriginIpAddresses] = useState([]);
  const [ipAddresses, setIpAddresses] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [selectedIpAddress, setSelectedIpAddress] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const pendingRequestRef = useRef({
    delete: false,
  });

  const fetchTableData = async () => {
    setLoading(true);
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(apiKey, uuidList.read, params);
  };

  const handleSearchChange = (string) => {
    setSearchVal(string);

    if (string.trim() === "") {
      setIpAddresses(cloneDeep(originIpAddresses));
      return;
    }

    const filteredData = originIpAddresses.filter((item) => {
      const nameMatch = item.ipGroupName.toLowerCase().includes(string.toLowerCase());
      const ipMatch = item.ref.ipList.some(ipItem =>
        ipItem.ipAddress.toLowerCase().includes(string.toLowerCase())
      );
      return nameMatch || ipMatch;
    });

    setIpAddresses(filteredData);
  };

  const createAction = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedIpAddress(null);
    setOpenModal(false);
  };

  const handleSaveChanges = () => {
    handleCloseModal();
    fetchTableData();
  };

  const editIpAddressProfile = (data) => {
    setSelectedIpAddress(data);
    setOpenModal(true);
  };

  const deleteIpAddressProfile = (data) => {
    setSelectedIpAddress(data);
    setOpenRemoveModal(true);
  };

  const handleCloseRemoveModal = () => {
    setSelectedIpAddress(null);
    setOpenRemoveModal(false);
  };

  const handleDeleteIpAddress = () => {
    setLoading(true);
    pendingRequestRef.current.delete = true;
    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.delete);

    sendRequest(
      apiKey,
      uuidList.delete,
      params,
      {
        profileIpAddrsIds: [selectedIpAddress.id],
      }
    );

    handleCloseRemoveModal();
  };

  useEffect(() => {
    if (isAuthReady && serviceStatus) {
      fetchTableData();
    }
  }, [isAuthReady, serviceStatus]);

  useEffect(() => {
    const readResponse = getData(uuidList.read);

    if (readResponse) {
      if (readResponse.hasOwnProperty("code")) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: readResponse?.message,
        });
        setLoading(false);
      } else {
        const updatedIpAddresses = [];
        const data = readResponse.data || [];

        for (let i = 0; i < data.length; i++) {
          const item = fromJSON(data[i]);
          updatedIpAddresses.push(item);
        }

        setOriginIpAddresses(cloneDeep(updatedIpAddresses));
        setIpAddresses(updatedIpAddresses);
        onIpAddressProfileCountChange?.(updatedIpAddresses.length);
        setLoading(false);
      }
    }
  }, [dataMap?.[uuidList.read], onIpAddressProfileCountChange]);

  useEffect(() => {
    const deleteResponse = getData(uuidList.delete);

    if (deleteResponse && pendingRequestRef.current.delete) {
      pendingRequestRef.current.delete = false;

      if (deleteResponse.hasOwnProperty("code") && deleteResponse?.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: deleteResponse?.message,
        });
        setLoading(false);
      } else {
        fetchTableData();
      }
    }
  }, [dataMap?.[uuidList.delete]]);

  const columnsData = getTableColumns(
    theme,
    columnHelper,
    showEditGeneral,
    editIpAddressProfile,
    deleteIpAddressProfile
  );
  const table = useTableInstance({
    tableName: "profilesIpAddressTable",
    data: ipAddresses,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  if (loading || !serviceStatus) {
    return <Loading />;
  }

  return (
    <>
      <Box sx={{ padding: "20px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            height: "80px",
            gap: 3,
          }}
        >
          <Divider
            sx={{ width: "1px", height: "40px", margin: "20px 0" }}
            orientation="vertical"
            flexItem
          />

          <SearchInput
            value={searchVal}
            placeholder="Search by IP Address, Name"
            onChange={handleSearchChange}
          />
          {showEditGeneral && (
            <SolidButton
              sx={{ whiteSpace: "nowrap" }}
              startIcon={<AddIcon />}
              width="auto"
              height="35px"
              onClick={createAction}
            >
              Create IP Address Profile
            </SolidButton>
          )}
        </Box>

        <TableField
          {...table}
          pendingChildren={loading && <Loading />}
        />

        <ProfileIpAddressModal
          auth={auth}
          uuidList={uuidList}
          openModal={openModal}
          selectedIpAddress={selectedIpAddress}
          showEditGeneral={showEditGeneral}
          isUnityProSubscribed={serviceStatus?.services?.unityPro === true}
          handleCloseModal={handleCloseModal}
          onSaveChanges={handleSaveChanges}
        />

        <ConfirmDeleteModal
          removeModal={openRemoveModal}
          onOpenRemoveModal={handleCloseRemoveModal}
          label={selectedIpAddress ? selectedIpAddress.ipGroupName : ""}
          removeCallback={handleDeleteIpAddress}
        />

        <GlobalAlert
          open={alertInfo.open}
          severity={alertInfo.severity}
          message={alertInfo.message}
          onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
        />
      </Box>
    </>
  );
};

export default ProfileIpAddress;
